import { client } from "./supabase";

/**
 * Obtener cotizaciones guardadas del usuario actual.
 * - CLIENTE: solo sus propias cotizaciones (por usuario_id)
 * - AGENCIA: cotizaciones de todos los usuarios de su misma agencia
 */
export const getMisCotizaciones = async (pais, usuarioId, rol, agenciaId) => {
  if (rol === "agencia" && agenciaId) {
    // Obtener IDs de todos los usuarios de esta agencia
    const { data: agenciaUsers, error: usersError } = await client
      .from("usuarios")
      .select("id")
      .eq("agencia", agenciaId)
      .eq("pais", pais);
    if (usersError) throw usersError;

    const userIds = (agenciaUsers || []).map((u) => u.id);
    if (userIds.length === 0) return [];

    const { data, error } = await client
      .from("cotizaciones")
      .select("*")
      .eq("pais", pais)
      .eq("eliminada", false)
      .in("usuario_id", userIds)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  }

  // CLIENTE u otros roles: solo sus propias cotizaciones
  const { data, error } = await client
    .from("cotizaciones")
    .select("*")
    .eq("pais", pais)
    .eq("usuario_id", usuarioId)
    .eq("eliminada", false)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Guardar una nueva cotización.
 */
export const guardarCotizacion = async (cotizacionData) => {
  const { data, error } = await client.from("cotizaciones").insert([cotizacionData]).select();
  if (error) throw error;
  return data?.[0];
};

/**
 * Obtener una cotización por ID.
 */
export const getCotizacionById = async (id) => {
  const { data, error } = await client.from("cotizaciones").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
};

export const getCotizacionesByCodigos = async (codigos) => {
  if (!codigos || codigos.length === 0) return [];
  const { data, error } = await client.from("cotizaciones").select("*").in("codigo_generado", codigos);
  if (error) throw error;
  return data || [];
};

/**
 * Eliminar cotización (soft delete). No permite eliminar ejecutadas.
 */
export const eliminarCotizacion = async (id) => {
  // Verificar que no esté ejecutada
  const cotizacion = await getCotizacionById(id);
  if (cotizacion.estado === "ejecutada") {
    throw new Error("No se puede eliminar una cotización ejecutada");
  }

  const { error } = await client
    .from("cotizaciones")
    .update({ eliminada: true, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

/**
 * Actualizar campos de una cotización (ej: asociar cliente).
 */
export const actualizarCotizacion = async (id, updates) => {
  const { data, error } = await client
    .from("cotizaciones")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

/**
 * Crear nueva versión de una cotización existente.
 * Lee la original para obtener version_grupo y version, luego inserta nueva fila.
 */
export const crearNuevaVersion = async (cotizacionOriginalId, nuevaData) => {
  const original = await getCotizacionById(cotizacionOriginalId);

  const versionGrupo = original.version_grupo || original.codigo_generado;
  const nuevaVersion = (original.version || 1) + 1;

  const { data, error } = await client
    .from("cotizaciones")
    .insert([
      {
        ...nuevaData,
        codigo_generado: original.codigo_generado,
        version: nuevaVersion,
        version_grupo: versionGrupo,
        estado: "borrador",
        eliminada: false,
      },
    ])
    .select();
  if (error) throw error;
  return data?.[0];
};

/**
 * Obtener TODAS las cotizaciones de un país (para panel interno Propuestas Externas).
 * No filtra por usuario — muestra todas las cotizaciones no eliminadas.
 */
export const getAllCotizacionesPais = async (pais) => {
  const { data, error } = await client
    .from("cotizaciones")
    .select("*")
    .eq("pais", pais)
    .eq("eliminada", false)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Obtener usuarios con info de agencia (para enriquecer cotizaciones con nombre de agencia).
 */
export const getUsuariosConAgencia = async (pais) => {
  const { data, error } = await client.from("usuarios").select("id, nombre, apellido, agencia, rol").eq("pais", pais);
  if (error) throw error;
  return data || [];
};

/**
 * Obtener clientes vinculados a una agencia (para dropdown de asociación).
 */
export const getClientesPorAgencia = async (pais, agenciaId) => {
  const { data, error } = await client
    .from("clientes")
    .select("id, nombre, empresa, producto")
    .eq("agencia", agenciaId)
    .eq("pais", pais)
    .eq("eliminada", false)
    .order("nombre", { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Obtener clientes de la cartera de un BROKER (2ª capa de intermediación).
 * Espejo de getClientesPorAgencia pero filtra por broker_id. Usado para el scoping
 * del rol BROKER (que solo ve su cartera). Si brokerId es falsy, devuelve [] (fail-safe).
 */
export const getClientesPorBroker = async (pais, brokerId) => {
  if (!brokerId) return [];
  const { data, error } = await client
    .from("clientes")
    .select("id, nombre, empresa, producto")
    .eq("broker_id", brokerId)
    .eq("pais", pais)
    .eq("eliminada", false)
    .order("nombre", { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Obtener clientes donde el ejecutivo de agencia (userId) es contacto.
 * Busca primero en tabla relacional clientes_contactos_agencia, fallback a campos legacy.
 */
export const getClientesPorEjecutivo = async (pais, userId) => {
  // Buscar en tabla relacional primero
  const { data: ccaRows, error: ccaError } = await client
    .from("clientes_contactos_agencia")
    .select("cliente_id")
    .eq("usuario_id", userId);

  if (!ccaError && ccaRows && ccaRows.length > 0) {
    const clienteIds = ccaRows.map((r) => r.cliente_id);
    const { data, error } = await client
      .from("clientes")
      .select("id, nombre, empresa, producto")
      .eq("pais", pais)
      .eq("eliminada", false)
      .in("id", clienteIds)
      .order("nombre", { ascending: true });
    if (error) throw error;
    return data || [];
  }

  // Fallback a campos legacy
  const { data, error } = await client
    .from("clientes")
    .select("id, nombre, empresa, producto")
    .eq("pais", pais)
    .eq("eliminada", false)
    .or(
      `contacto_agencia.eq.${userId},contacto_agencia_1.eq.${userId},contacto_agencia_2.eq.${userId},contacto_agencia_3.eq.${userId}`
    )
    .order("nombre", { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Upsert por código: si ya existe una fila no-eliminada con ese codigo_generado,
 * actualiza la más reciente (mayor id). Si no, inserta nueva.
 * Tolera los duplicados históricos eligiendo la última.
 */
export const upsertCotizacionPorCodigo = async (codigoGenerado, updates) => {
  const { data: existentes } = await client
    .from("cotizaciones")
    .select("id")
    .eq("codigo_generado", codigoGenerado)
    .eq("eliminada", false)
    .order("id", { ascending: false })
    .limit(1);

  if (existentes && existentes.length > 0) {
    return actualizarCotizacion(existentes[0].id, updates);
  }
  return guardarCotizacion({ ...updates, codigo_generado: codigoGenerado });
};
